import { useEffect, useState, useCallback } from "react";
import izenuAPI from "../../services/izenuAPI";

/**
 * Artistas em Destaque — a curadoria da plataforma.
 *
 * Não é um ranking. A ordenação por audiência vive na home, calculada, e esta
 * secção existe para o caso oposto: apostar num artista que os números ainda
 * não conseguem ver. É a mesma decisão já tomada para os `featured` da loja.
 *
 * Cada destaque tem validade. Até 11/09/2026 ninguém a lia, e por isso seis
 * destaques criados num único dia de Abril de 2025 continuavam na home mais de
 * um ano depois de terem expirado — um deles com validade cinco segundos
 * anterior à própria criação. Agora expiram sozinhos.
 */

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const displayName = (a) =>
    a.artist_name || a.name || [a.first_name, a.last_name].filter(Boolean).join(" ") || `#${a.user_id}`;

function photoUrl(userId, photo) {
    if (!photo) return null;
    return `https://dpagz47n4g1lw.cloudfront.net/users/${userId}/profile_photos/${photo}`;
}

function Avatar({ userId, photo, name }) {
    const [failed, setFailed] = useState(false);
    const url = photoUrl(userId, photo);
    if (!url || failed) {
        return (
            <div style={{ ...s.avatar, ...s.avatarFallback }}>
                {(name || "?").trim().charAt(0).toUpperCase()}
            </div>
        );
    }
    return <img src={url} alt="" style={s.avatar} onError={() => setFailed(true)} />;
}

export default function FeaturedArtists() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [picked, setPicked] = useState(null);
    const [endDate, setEndDate] = useState(inDays(30));

    const fetchRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await izenuAPI.get("/admin/artist_promoted/list.php");
            setRows(data?.data || []);
        } catch (e) {
            setError("Não foi possível carregar os destaques.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const runSearch = async () => {
        if (!search.trim()) return;
        setSearching(true);
        try {
            const p = new URLSearchParams({ search: search.trim(), limit: "10" });
            const { data } = await izenuAPI.get(`/admin/artist/list.php?${p.toString()}`);
            setResults(data?.data || data?.artists || []);
        } catch (e) {
            setError("Não foi possível procurar artistas.");
        } finally {
            setSearching(false);
        }
    };

    const save = async (payload) => {
        setSaving(true);
        setError(null);
        try {
            await izenuAPI.post("/admin/artist_promoted/save.php", payload);
            setPicked(null);
            setSearch("");
            setResults([]);
            await fetchRows();
        } catch (e) {
            setError(e?.response?.data?.message || "Não foi possível gravar.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        setSaving(true);
        try {
            await izenuAPI.post("/admin/artist_promoted/delete.php", { id });
            await fetchRows();
        } catch (e) {
            setError("Não foi possível remover.");
        } finally {
            setSaving(false);
        }
    };

    const active = rows.filter((r) => Number(r.is_active) === 1);
    const expired = rows.filter((r) => Number(r.is_active) !== 1);

    return (
        <div className="sm:p-6 text-left" style={s.container}>
            <div style={s.headerRow}>
                <div>
                    <h2 style={s.title}>Artistas em Destaque</h2>
                    <p style={s.subtitle}>
                        Escolhe os artistas que aparecem na secção «Artistas em Destaque», na home da
                        app e do site. Cada destaque tem uma data de fim e sai da home sozinho quando
                        ela passa; sem nenhum activo, a secção não aparece. É uma escolha editorial da
                        Izenu e não mexe nos «Artistas Populares», que são calculados pela audiência.
                    </p>
                </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            <div style={s.card}>
                <h3 style={s.cardTitle}>Adicionar destaque</h3>
                <p style={s.cardDesc}>
                    Procura o artista, escolhe até quando fica em destaque e grava. Quando a data
                    passar, sai da home sozinho.
                </p>

                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 260px" }}>
                        <label style={s.label}>Artista</label>
                        <input
                            style={s.input}
                            placeholder="Nome ou email"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && runSearch()}
                        />
                    </div>
                    <button style={s.chipBtn} onClick={runSearch} disabled={searching}>
                        {searching ? "A procurar…" : "Procurar"}
                    </button>
                    <div style={{ flex: "0 0 170px" }}>
                        <label style={s.label}>Em destaque até</label>
                        <input type="date" style={s.input} min={today()}
                            value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                        <button style={s.chipBtn} onClick={() => setEndDate(inDays(15))}>15d</button>
                        <button style={s.chipBtn} onClick={() => setEndDate(inDays(30))}>30d</button>
                        <button style={s.chipBtn} onClick={() => setEndDate(inDays(90))}>90d</button>
                    </div>
                </div>

                {results.length > 0 && (
                    <div style={s.resultsBox}>
                        {results.map((a) => {
                            const isPicked = picked?.user_id === a.user_id;
                            return (
                                <div key={a.user_id}
                                    style={{ ...s.resultRow, background: isPicked ? "#eef2ff" : undefined }}
                                    onClick={() => setPicked(a)}>
                                    <Avatar userId={a.user_id} photo={a.photo} name={displayName(a)} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={s.itemTitle}>{displayName(a)}</div>
                                        <div style={s.itemSub}>{a.email || `#${a.user_id}`}</div>
                                    </div>
                                    {isPicked && (
                                        <button style={s.primaryBtn} disabled={saving}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                save({ user_id: a.user_id, end_date: endDate });
                                            }}>
                                            {saving ? "A gravar…" : "Destacar"}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div style={s.card}>
                <h3 style={s.cardTitle}>Activos ({active.length})</h3>
                <p style={s.cardDesc}>
                    É isto que aparece na home, ordenado por quem expira primeiro — assim um
                    destaque a terminar aproveita os últimos dias de visibilidade.
                </p>
                {loading && <p style={s.empty}>A carregar…</p>}
                {!loading && active.length === 0 && (
                    <p style={s.empty}>
                        Nenhum destaque activo. A secção "Artistas em Destaque" não aparece na home
                        enquanto assim for.
                    </p>
                )}
                {active.map((r) => (
                    <Row key={r.id} row={r} saving={saving} onRemove={remove} onExtend={save} />
                ))}
            </div>

            {expired.length > 0 && (
                <div style={s.card}>
                    <h3 style={s.cardTitle}>Expirados ({expired.length})</h3>
                    <p style={s.cardDesc}>
                        Já não aparecem na home. Ficam aqui como histórico — "Renovar" volta a
                        pô-los em destaque sem recriar a linha.
                    </p>
                    {expired.map((r) => (
                        <Row key={r.id} row={r} saving={saving} onRemove={remove} onExtend={save} expired />
                    ))}
                </div>
            )}
        </div>
    );
}

function Row({ row, saving, onRemove, onExtend, expired }) {
    const [renewing, setRenewing] = useState(false);
    const [date, setDate] = useState(inDays(30));
    const name = displayName(row);

    return (
        <div style={{ ...s.listItem, opacity: expired ? 0.6 : 1 }}>
            <Avatar userId={row.user_id} photo={row.photo} name={name} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.itemTitle}>{name}</div>
                <div style={s.itemSub}>
                    {expired ? "Expirou" : "Até"} {String(row.end_date || "").slice(0, 10)}
                    {row.created_at ? ` · criado em ${String(row.created_at).slice(0, 10)}` : ""}
                </div>
            </div>

            {renewing ? (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="date" style={{ ...s.input, width: 150 }} min={today()}
                        value={date} onChange={(e) => setDate(e.target.value)} />
                    <button style={s.primaryBtn} disabled={saving}
                        onClick={() => { onExtend({ id: row.id, end_date: date }); setRenewing(false); }}>
                        Gravar
                    </button>
                    <button style={s.chipBtn} onClick={() => setRenewing(false)}>Cancelar</button>
                </div>
            ) : (
                <div style={{ display: "flex", gap: 6 }}>
                    <button style={s.chipBtn} onClick={() => setRenewing(true)}>
                        {expired ? "Renovar" : "Alterar data"}
                    </button>
                    <button style={s.dangerBtn} disabled={saving} onClick={() => onRemove(row.id)}>
                        Remover
                    </button>
                </div>
            )}
        </div>
    );
}

const s = {
    container: { maxWidth: 1100 },
    headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, textAlign: "left" },
    title: { fontSize: 22, color: "#222", margin: 0 },
    subtitle: { margin: "8px 0 0", fontSize: 14, color: "#555", fontWeight: 400, maxWidth: 720, lineHeight: 1.5, textAlign: "left" },
    card: { padding: 24, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 20 },
    cardTitle: { fontSize: 15, fontWeight: 600, color: "#333", margin: "0 0 4px" },
    cardDesc: { margin: "0 0 14px", fontSize: 12, color: "#aaa", lineHeight: 1.5, maxWidth: 700 },
    label: { display: "block", fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 5 },
    input: { width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#1f2937", outline: "none" },
    primaryBtn: { padding: "8px 14px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13 },
    chipBtn: { padding: "8px 12px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 },
    dangerBtn: { padding: "8px 12px", background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
    resultsBox: { marginTop: 16, border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" },
    resultRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: "1px solid #f5f5f5", cursor: "pointer" },
    listItem: { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f5f5f5" },
    avatar: { width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
    avatarFallback: { display: "flex", alignItems: "center", justifyContent: "center", background: "#e0e7ff", color: "#4338ca", fontWeight: 700, fontSize: 15 },
    itemTitle: { fontSize: 14, fontWeight: 600, color: "#1f2937" },
    itemSub: { fontSize: 12, color: "#9ca3af", marginTop: 2 },
    empty: { color: "#ccc", textAlign: "center", padding: 24, fontSize: 13 },
    errorBox: { padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 8, marginBottom: 16, fontSize: 13 },
};
